import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  AUDIENCES,
  audienceLabel,
  deleteTestimonial,
  getTestimonials,
  saveTestimonial,
} from "../../../store/actions/councilActions/faqAction";
import { errorAlert } from "../../../utils/alert";

/*
  Testimonials, council side.

  Split the same way as the FAQs, because the website shows them in the same
  three places: a student's words in the student section, a centre's in the
  centre section, and so on.
*/
const EMPTY = {
  audience: "student",
  name: "",
  designation: "",
  rating: 5,
  message: "",
  sort_order: 0,
  is_active: 1,
};

const TestimonialList = () => {
  const dispatch = useDispatch();

  const [list, setList] = useState(null);
  const [counts, setCounts] = useState({});
  const [audience, setAudience] = useState("student");
  const [form, setForm] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setList(null);
    const { list: rows, counts: totals } = await dispatch(
      getTestimonials({ audience })
    );
    setList(rows);
    setCounts(totals);
  }, [dispatch, audience]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return errorAlert("Please write the name.");
    if (!form.message.trim()) return errorAlert("Please write what they said.");

    setBusy(true);
    const body = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key !== "id") body.append(key, value === null ? "" : value);
    });
    if (photo) body.append("photo", photo);

    const done = await dispatch(saveTestimonial(body, form.id));
    setBusy(false);

    if (done) {
      setAudience(form.audience);
      setForm(null);
      setPhoto(null);
      load();
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete the testimonial from "${row.name}"?`)) return;
    if (await dispatch(deleteTestimonial(row.id))) load();
  };

  return (
    <div>
      <div class="d-flex justify-content-between align-items-center">
        <div class="BigTitle">Testimonials</div>
        <button
          type="button"
          class="btn btn-comman"
          onClick={() => {
            setForm({ ...EMPTY, audience });
            setPhoto(null);
          }}
        >
          Add Testimonial
        </button>
      </div>

      <ul class="nav nav-tabs mt-3">
        {AUDIENCES.map((a) => (
          <li class="nav-item" key={a.key}>
            <button
              type="button"
              class={`nav-link ${audience === a.key ? "active" : ""}`}
              onClick={() => setAudience(a.key)}
            >
              {a.label} ({counts[a.key] ?? 0})
            </button>
          </li>
        ))}
      </ul>

      <div class="table-responsive mt-3">
        <table class="table">
          <thead>
            <tr>
              <th style={{ width: "70px" }}>Order</th>
              <th style={{ width: "70px" }}>Photo</th>
              <th>Name</th>
              <th>Says</th>
              <th>Rating</th>
              <th>Status</th>
              <th style={{ width: "140px" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {list === null ? (
              <tr>
                <td colSpan={7}>Loading...</td>
              </tr>
            ) : list.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  No testimonial added for {audienceLabel(audience)} yet.
                </td>
              </tr>
            ) : (
              list.map((row) => (
                <tr key={row.id}>
                  <td>{row.sort_order}</td>
                  <td>
                    {row.photo ? (
                      <img src={row.photo} alt={row.name} height="40" />
                    ) : (
                      "--"
                    )}
                  </td>
                  <td>
                    {row.name}
                    {row.designation ? (
                      <div class="text-muted">
                        <small>{row.designation}</small>
                      </div>
                    ) : null}
                  </td>
                  <td>{String(row.message).slice(0, 90)}</td>
                  <td>{row.rating ? `${row.rating} / 5` : "--"}</td>
                  <td>
                    {Number(row.is_active) === 1 ? (
                      <span class="text-success">Live</span>
                    ) : (
                      <span class="text-muted">Hidden</span>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      class="btn btn-sm btn-light-primary mr-1"
                      onClick={() => {
                        setForm({
                          id: row.id,
                          audience: row.audience || "general",
                          name: row.name || "",
                          designation: row.designation || "",
                          rating: Number(row.rating) || "",
                          message: row.message || "",
                          sort_order: Number(row.sort_order) || 0,
                          is_active: Number(row.is_active),
                        });
                        setPhoto(null);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-danger"
                      onClick={() => remove(row)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal show={!!form} onHide={() => setForm(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {form?.id ? "Edit Testimonial" : "Add Testimonial"}
          </Modal.Title>
        </Modal.Header>
        {form && (
          <form onSubmit={submit}>
            <Modal.Body>
              <div class="row">
                <div class="col-md-4 mb-2">
                  <label>Show in *</label>
                  <select
                    class="form-control"
                    value={form.audience}
                    onChange={(e) =>
                      setForm({ ...form, audience: e.target.value })
                    }
                  >
                    {AUDIENCES.map((a) => (
                      <option value={a.key} key={a.key}>
                        {a.label} section
                      </option>
                    ))}
                  </select>
                </div>
                <div class="col-md-4 mb-2">
                  <label>Name *</label>
                  <input
                    type="text"
                    class="form-control"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>
                <div class="col-md-4 mb-2">
                  <label>Designation</label>
                  <input
                    type="text"
                    class="form-control"
                    placeholder="Student, DMLT · or the centre's name"
                    value={form.designation}
                    onChange={(e) =>
                      setForm({ ...form, designation: e.target.value })
                    }
                  />
                </div>

                <div class="col-md-4 mb-2">
                  <label>Rating</label>
                  <select
                    class="form-control"
                    value={form.rating}
                    onChange={(e) =>
                      setForm({ ...form, rating: e.target.value })
                    }
                  >
                    <option value="">No stars</option>
                    {[5, 4, 3, 2, 1].map((n) => (
                      <option value={n} key={n}>
                        {n} / 5
                      </option>
                    ))}
                  </select>
                </div>
                <div class="col-md-2 mb-2">
                  <label>Order</label>
                  <input
                    type="number"
                    class="form-control"
                    value={form.sort_order}
                    onChange={(e) =>
                      setForm({ ...form, sort_order: e.target.value })
                    }
                  />
                </div>
                <div class="col-md-2 mb-2">
                  <label>Status</label>
                  <select
                    class="form-control"
                    value={form.is_active}
                    onChange={(e) =>
                      setForm({ ...form, is_active: Number(e.target.value) })
                    }
                  >
                    <option value={1}>Live</option>
                    <option value={0}>Hidden</option>
                  </select>
                </div>
                <div class="col-md-4 mb-2">
                  <label>Photo</label>
                  <input
                    type="file"
                    class="form-control"
                    accept="image/*"
                    onChange={(e) => setPhoto(e.target.files[0])}
                  />
                  {form.id && !photo && (
                    <small class="text-muted">
                      Leave empty to keep the current photo.
                    </small>
                  )}
                </div>

                <div class="col-md-12 mb-2">
                  <label>What they said *</label>
                  <textarea
                    class="form-control"
                    rows={4}
                    value={form.message}
                    onChange={(e) =>
                      setForm({ ...form, message: e.target.value })
                    }
                    required
                  />
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                class="btn btn-secondary"
                onClick={() => setForm(null)}
              >
                Cancel
              </button>
              <button type="submit" class="btn btn-comman" disabled={busy}>
                {busy ? "Saving..." : "Save"}
              </button>
            </Modal.Footer>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default TestimonialList;
